import React, { useState } from 'react';
import BentoCard from '../components/BentoCard';
import { Link } from 'react-router-dom';

// Asset Imports
import heroBg from '../images/homepageBackground.jpg';
import sponsorsOne from '../images/homepagePics/vendorsAndSponsors1.jpg';
import sponsorsTwo from '../images/homepagePics/vendorsAndSponsors2.jpg';
import fundayVideoThumbnail from '../images/events/FunDayThumbnail.jpg';

// Press Release Import
import pressReleaseImg from '../images/homepagePics/PositiveSomebodyPressRelease.jpg';

import { splitEvents, shortDate } from '../configs/eventInfo';

const Home = () => {
  // State for Video Modal - holds the src of the video currently playing, or null when closed
  const [activeVideo, setActiveVideo] = useState(null);

  // State for Image Modal (Lightbox)
  const [selectedImage, setSelectedImage] = useState(null);

  // Events come from src/configs/eventInfo.js, the same source /events reads,
  // so these sections re-sort themselves as dates pass.
  const { upcoming, past } = splitEvents();
  const nextEvent = upcoming[0];
  const lastEvent = past[0];

  // Up to three flyers for the next events. Tailwind's JIT can't see
  // interpolated class names, so the column count is a literal lookup.
  const flyerEvents = upcoming.slice(0, 3);
  const flyerColsClass = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3" }[flyerEvents.length] || "md:grid-cols-3";
  const flyerCtaClass = "block w-full mt-3 px-6 py-3 rounded-full text-center bg-accent-yellow text-primary-navy font-heading font-bold text-sm uppercase tracking-wide border-2 border-primary-navy shadow-[4px_4px_0px_#020617] hover:bg-yellow-300 hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#020617] transition-all active:translate-x-[2px] active:translate-y-[2px] active:shadow-none";

  return (
    <main className="max-w-7xl mx-auto p-4 lg:p-8 space-y-8">

      {/* --- VIDEO MODAL (Light Box) --- */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] bg-primary-navy/90 backdrop-blur-md flex items-center justify-center p-4 transition-all duration-300"
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="relative w-full max-w-5xl aspect-video bg-black rounded-2xl overflow-hidden shadow-[8px_8px_0px_#000000] border-4 border-white"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 z-50 text-white bg-black/50 hover:bg-accent-orange rounded-full p-2 transition-colors"
              onClick={() => setActiveVideo(null)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <video controls autoPlay playsInline className="w-full h-full object-cover">
              <source src={activeVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}

      {/* --- IMAGE MODAL (Light Box) --- */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <img
            src={selectedImage}
            alt="Full view"
            className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl cursor-default"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* SECTION 1: HERO GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Hero Section */}
        <BentoCard
          colSpan="lg:col-span-2"
          className="overflow-hidden p-0 relative min-h-[400px] group !bg-[#00F0FF]/10 backdrop-blur-sm"
        >
          <img
            src={heroBg}
            alt="Positive Somebody Hero"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 grayscale-[30%] opacity-60 mix-blend-multiply contrast-125"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-primary-navy/90 via-primary-navy/20 to-transparent z-10"></div>

          <div className="absolute bottom-0 left-0 p-8 z-20 w-full">
            <h1 className="text-4xl md:text-5xl font-heading font-black text-white mb-4 drop-shadow-md">
              Building Resilience Through Positive Coping
            </h1>
            <button
              onClick={() => setActiveVideo("/videos/comercial.mp4")}
              className="
                bg-accent-yellow text-primary-navy 
                font-heading font-black uppercase tracking-wider
                py-3 px-8 rounded-full 
                border-2 border-white
                shadow-[4px_4px_0px_#ffffff40]
                hover:bg-white hover:scale-105 hover:shadow-none
                active:translate-y-[2px] active:translate-x-[2px]
                transition-all flex items-center gap-2
              "
            >
              <span>▶</span> Watch Our Story
            </button>
          </div>
        </BentoCard>

        {/* OUR MISSION (Swapped from Section 2) */}
        <BentoCard
          colSpan="lg:col-span-1"
          className="!bg-primary-navy text-white border-primary-navy relative overflow-hidden flex flex-col justify-center p-8"
        >
          <div className="absolute -bottom-8 -right-8 text-9xl opacity-20 pointer-events-none select-none rotate-12">
            🛡️
          </div>

          <h3 className="font-heading text-2xl font-bold mb-4 text-accent-cyan relative z-10">
            Our Mission
          </h3>

          <div className="relative z-10">
            <p className="text-lg leading-relaxed text-blue-50 font-medium">
              To build emotionally stronger communities by teaching emotional awareness and positive coping skills. We focus on giving kids and families the confidence to recognize their emotions.
            </p>
          </div>
        </BentoCard>
      </div>

      {/* SECTION 1.5: FAMILY FUN DAY 2026 RECAP */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Video Card */}
        <BentoCard
          colSpan="lg:col-span-2"
          className="p-4 flex items-center justify-center bg-white min-h-[300px] lg:min-h-[400px]"
        >
          <div
            className="relative group cursor-pointer w-full h-full flex items-center justify-center overflow-hidden rounded-lg"
            onClick={() => setActiveVideo("/videos/Family_Fun_Day_2026.mp4")}
          >
            <img
              src={fundayVideoThumbnail}
              alt="5th Annual Family Mental Health Fun Day - June 20th at Potter Park Zoo"
              className="w-full h-full object-contain"
            />

            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors">
              <span
                className="
                  flex items-center justify-center
                  w-20 h-20 rounded-full
                  bg-accent-yellow text-primary-navy text-3xl
                  border-2 border-primary-navy
                  shadow-[4px_4px_0px_#020617]
                  group-hover:scale-110
                  transition-transform
                "
              >
                ▶
              </span>
            </div>
          </div>
        </BentoCard>

        {/* Recap Card */}
        <BentoCard
          colSpan="lg:col-span-1"
          className="!bg-primary-navy text-white border-primary-navy relative overflow-hidden flex flex-col justify-center p-8"
        >
          <span className="inline-block text-accent-yellow font-heading font-bold uppercase tracking-widest text-xs mb-3">
            Event Recap
          </span>

          <h3 className="font-heading text-2xl font-bold mb-4 text-white">
            5th Annual Family Mental Health Fun Day
          </h3>

          <p className="text-sm leading-relaxed text-blue-100 mb-6">
            Families spent the day with us at Potter Park Zoo for a free, festival-style afternoon of hands-on mental health activities, community resources, and fun for all ages. Press play to look back on the day.
          </p>

          <ul className="space-y-4 mb-6">
            <li className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-xl shrink-0">📅</div>
              <p className="font-bold text-white text-sm">June 20, 2026</p>
            </li>
            <li className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-xl shrink-0">📍</div>
              <p className="font-bold text-white text-sm">Potter Park Zoo</p>
            </li>
          </ul>

          <Link to="/events" className="flex items-center gap-2 text-accent-cyan font-bold text-sm hover:text-white transition-colors group">
            See More Events
            <span className="transform group-hover:translate-x-1 transition-transform">→</span>
          </Link>
        </BentoCard>
      </div>

      {/* SECTION 2: CALENDAR & UPDATED LATEST UPDATES */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        {/* LATEST UPDATES (Swapped from Section 1 & Refactored) */}
        <BentoCard title="Latest Updates" colSpan="lg:col-span-1" color="bg-white" className="flex flex-col h-full border-2 border-gray-100 shadow-sm">
          <div className="flex flex-col gap-4 h-full">

            {/* Featured Events List - next up and most recent, by date */}
            <div className="space-y-3">
              {nextEvent && (
                <div className="bg-blue-50 p-4 rounded-xl border-l-4 border-accent-orange group hover:bg-blue-100 transition-colors">
                  <span className="text-[10px] font-bold text-accent-orange uppercase tracking-widest">Upcoming / {shortDate(nextEvent.date)}</span>
                  <p className="font-bold text-primary-navy text-sm leading-tight">{nextEvent.title}</p>
                </div>
              )}

              {lastEvent && (
                <div className="bg-blue-50 p-4 rounded-xl border-l-4 border-primary-navy group hover:bg-blue-100 transition-colors">
                  <span className="text-[10px] font-bold text-primary-navy uppercase tracking-widest">Recent / {shortDate(lastEvent.date)}</span>
                  <p className="font-bold text-primary-navy text-sm leading-tight">{lastEvent.title}</p>
                </div>
              )}
            </div>

            {/* Impact Section - Retained and Highlighted */}
            <div className="mt-2 bg-green-50 p-4 rounded-xl border border-green-100 shadow-inner">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold text-green-700 uppercase tracking-wider">Our Impact</span>
              </div>
              <p className="font-bold text-primary-navy text-sm">
                We have reached over <span className="text-green-600 text-lg">2,000</span> students in the last month.
              </p>
            </div>

            {/* Link to Events */}
            <div className="mt-auto pt-4 border-t border-gray-100">
              <Link to="/events" className="flex items-center justify-between text-primary-navy font-bold text-sm hover:text-accent-orange transition-colors group">
                View All Events
                <span className="transform group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
          </div>
        </BentoCard>

        {/* CALENDAR */}
        <BentoCard title="Upcoming Events" colSpan="md:col-span-2" className="min-h-[400px] p-0 overflow-hidden relative border-2 border-gray-100 shadow-sm">
          <iframe
            src="https://calendar.google.com/calendar/embed?src=4ab281c3475bdb1d1437e1757b35cc7279b76f90b01402e7a826af10cfc9244a%40group.calendar.google.com&ctz=America%2FDetroit"
            style={{ border: 0 }}
            width="100%"
            height="100%"
            frameBorder="0"
            scrolling="no"
            title="Positive Somebody Calendar"
            className="w-full h-full"
          ></iframe>
        </BentoCard>
      </div>

      {/* Flyers for whatever is coming up next, soonest first. Past events
          drop off on their own - nothing here needs pruning by hand. */}
      {flyerEvents.length > 0 && (
        <div className={`grid grid-cols-1 ${flyerColsClass} gap-3`}>
          {flyerEvents.map((event) => (
            <BentoCard key={event.id}>
              {/* object-contain so portrait and landscape flyers both keep
                  their aspect ratio - the card's white bg hides the letterbox. */}
              <img src={event.image} alt={`${event.title} flyer preview`} className="w-full h-96 object-contain rounded-md" />
              {event.ticketUrl || event.pdfUrl ? (
                <a href={event.ticketUrl || event.pdfUrl} target="_blank" rel="noopener noreferrer"
                  className={flyerCtaClass}
                >
                  {event.ticketUrl ? "Get Tickets ↗" : "Open PDF ↗"}
                </a>
              ) : (
                <Link to="/events" className={flyerCtaClass}>
                  Event Details →
                </Link>
              )}
            </BentoCard>
          ))}
        </div>
      )}


      {/* SECTION 3: SPLIT CTA */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* GET HELP */}
        <BentoCard className="!bg-[#00F0FF]/10 border-accent-cyan text-primary-navy flex flex-col items-center text-center justify-center min-h-[300px]">
          <span className="text-6xl mb-4">🤝</span>
          <h2 className="text-4xl font-heading font-black mb-2 text-[#0EA5E9]">Get Help</h2>
          <p className="mb-8 max-w-sm text-lg font-medium">Bring Positive Packs or &quot;The Weather Within&quot; program to your school.</p>
          <a href="/programs" className="bg-primary-navy text-white py-3 px-8 rounded-full font-heading font-bold shadow-[4px_4px_0px_#0EA5E9] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#0EA5E9] transition-all">
            View Programs
          </a>
        </BentoCard>

        {/* GIVE HELP */}
        <BentoCard className="!bg-[#FF5E1E]/10 border-accent-orange text-primary-navy flex flex-col items-center text-center justify-center min-h-[300px]">
          <span className="text-6xl mb-4">❤️</span>
          <h2 className="text-4xl font-heading font-black mb-2 text-accent-orange">Give Help</h2>
          <p className="mb-8 max-w-sm text-lg font-medium">Support our mission to create emotionally healthy communities.</p>
          <a href="/donate" className="bg-accent-orange text-white py-3 px-8 rounded-full font-heading font-bold shadow-[4px_4px_0px_#020617] hover:bg-orange-600 hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#020617] transition-all">
            Donate Now
          </a>
        </BentoCard>
      </div>

      {/* SECTION 4: IN THE NEWS */}
      <BentoCard className="bg-white border-2 border-gray-100 overflow-hidden">
        <div className="flex flex-col md:flex-row gap-8 items-center">
          <div className="flex-1 p-2 md:p-4 text-center md:text-left">
            <span className="inline-block px-3 py-1 bg-red-50 text-red-600 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
              Press Release
            </span>
            <h3 className="font-heading font-bold text-3xl text-primary-navy mb-4">Making Headlines</h3>
            <p className="text-gray-600 mb-6 leading-relaxed font-medium">
              We are proud to share our journey and impact with the Mid-Michigan community.
            </p>
            <button
              onClick={() => setSelectedImage(pressReleaseImg)}
              className="inline-flex items-center gap-2 text-primary-navy font-bold hover:gap-3 transition-all border-b-2 border-accent-yellow pb-1 hover:border-accent-orange"
            >
              Read Full Release <span>→</span>
            </button>
          </div>

          <div
            className="flex-1 w-full max-w-md cursor-pointer group relative shadow-lg rounded-xl overflow-hidden border border-gray-100"
            onClick={() => setSelectedImage(pressReleaseImg)}
          >
            <img
              src={pressReleaseImg}
              alt="Positive Somebody Press Release"
              className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors">
              <span className="opacity-0 group-hover:opacity-100 bg-white px-6 py-3 rounded-full text-primary-navy font-bold shadow-xl transform translate-y-4 group-hover:translate-y-0 transition-all">
                🔍 Click to Read
              </span>
            </div>
          </div>
        </div>
      </BentoCard>

      {/* SECTION 5: SPONSORS & VENDORS */}
      <BentoCard className="bg-white text-center py-8">
        <h3 className="text-gray-400 font-bold uppercase tracking-widest text-sm mb-6">With Gratitude To Our Community Sponsors & Vendors</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <img src={sponsorsOne} alt="Sponsors Part 1" className="rounded-xl w-full h-auto shadow-sm border border-gray-100" />
          <img src={sponsorsTwo} alt="Sponsors Part 2" className="rounded-xl w-full h-auto shadow-sm border border-gray-100" />
        </div>
      </BentoCard>

    </main>
  );
}

export default Home;